import { Component, Inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import firebase from 'firebase/app';
import { Facility } from '../../../services/firebase-utilities.service';
import { UIMessagingService } from '../../../services/uimessaging.service';
import { AuthService } from '../../../services/auth.service';
import { MailerService } from '../../../services/mailer.service';
import { UserRoles } from '../../../dictionaries/UserRoles';
import { UserPlans } from '../../../dictionaries/UserPlans';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-invite-facility-dialog',
  templateUrl: './invite-facility-dialog.component.html',
  styleUrls: ['./invite-facility-dialog.component.scss'],
  standalone: false
})
export class InviteFacilityDialogComponent {
  facility: Facility;
  hubFacilityName: string;
  inviteForm: UntypedFormGroup;
  sending = false;
  mode: 'invite' | 'send' = 'invite';
  dbFacilities = firebase.firestore().collection('facilities');
  invitationLink = '';
  linkCopied = false;

  constructor(
    public dialogRef: MatDialogRef<InviteFacilityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { facility: Facility; mode?: 'invite' | 'send'; hubFacilityName?: string; prefillEmail?: string },
    private uiMessaging_$: UIMessagingService,
    private auth_$: AuthService,
    private mailer_$: MailerService,
    private http: HttpClient
  ) {
    this.facility = data.facility;
    this.mode = data.mode || 'invite';
    this.hubFacilityName = data.hubFacilityName || 'NuageDX';
    const prefillEmail = data.prefillEmail || '';

    if (this.mode === 'send') {
      this.inviteForm = new UntypedFormGroup({
        name: new UntypedFormControl('', [Validators.required]),
        email: new UntypedFormControl(prefillEmail, [Validators.required, Validators.email]),
      });
    } else {
      this.inviteForm = new UntypedFormGroup({
        email: new UntypedFormControl(prefillEmail, [Validators.required, Validators.email])
      });
    }
  }

  async onSend(): Promise<void> {
    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      return;
    }

    this.sending = true;
    const email = (this.inviteForm.get('email')!.value as string).toLowerCase().trim();

    try {
      if (this.mode === 'send') {
        await this.sendOwnerInvitation(email);
      } else {
        await new Promise(resolve => setTimeout(resolve, 800));
        this.uiMessaging_$.toastMessage(
          `Invitation sent to ${email} for ${this.facility.name}.`, 'OK'
        );
        this.dialogRef.close({ sent: true, email });
      }
    } catch (error: any) {
      this.sending = false;
      const msg = error?.error?.message || error?.message || 'An error occurred. Please try again.';
      this.uiMessaging_$.toastMessage(`Error: ${msg}`, 'OK');
    }
  }

  private async sendOwnerInvitation(email: string): Promise<void> {
    const name = (this.inviteForm.get('name')!.value as string).trim();
    const currentUserUid = this.auth_$.auth.currentUser?.uid;
    if (!currentUserUid) throw new Error('Not authenticated');

    const spokeFacilityName = this.facility.name || this.facility.id || 'your facility';

    // Step 1: Create a minimal user account with ownerID pre-set (CF runs as admin — no client Firestore write needed)
    const createdUserResponse: any = await firstValueFrom(
      this.auth_$.callCloudFunction('admin-createAppUserV4', {
        password: this.generateRandomPassword(),
        email,
        facility: this.facility.id?.toLowerCase() || null,
        facilityuser: false,
        role: UserRoles.owner,
        useruid: currentUserUid,
        cellphone: '',
        displayName: name,
        ownerID: null,
        specialty: '',
        owners: [],
        companyName: '',
        firstname: name.split(' ')[0] || name,
        lastname: name.split(' ').slice(1).join(' ') || '',
        clientMatter: '',
        plancode: UserPlans.spoke
      }, 'POST')
    );

    const createdUid: string = createdUserResponse?.uid
      || createdUserResponse?.body?.uid
      || createdUserResponse?.data?.uid;
    if (!createdUid) throw new Error('User creation succeeded but UID was not returned.');

    // Step 2: Set ownerID on the facility doc (Hub users are allowed to update facilities they manage)
    await this.dbFacilities.doc(this.facility.id).update({ ownerID: createdUid });

    // Step 3: Set ownerID on user doc + generate invitation token — CF runs as admin, no permission issues
    const { invitationToken } = await firstValueFrom(
      this.http.post<{ success: boolean; invitationToken?: string }>(
        `${environment.constants.cloudfunctionsURL}admin-updateUserProfile`,
        {
          uid: createdUid,
          name,
          email,
          ownerID: createdUid,
          owners: [createdUid],
          invitation: { facilityId: this.facility.id || null, facilityName: spokeFacilityName }
        }
      )
    );
    if (!invitationToken) throw new Error('Invitation token was not returned.');

    // Step 4: Send invitation email
    await this.mailer_$.sendFacilityNetworkInvitation({
      recipientEmail: email,
      spokeFacilityName,
      hubFacilityName: this.hubFacilityName,
      invitationToken,
    });

    this.sending = false;
    this.invitationLink = `https://medical.nuagedx.com/join?token=${invitationToken}`;
    this.uiMessaging_$.toastMessage(
      `Invitation sent to ${email}. They will complete registration via the link in the email.`,
      'SUCCESS'
    );
  }

  private generateRandomPassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=';
    return Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }

  copyLink(): void {
    navigator.clipboard.writeText(this.invitationLink).then(() => {
      this.linkCopied = true;
    });
  }

  onCancel(): void {
    if (this.invitationLink) {
      const email = this.inviteForm.get('email')?.value;
      this.dialogRef.close({ created: true, email });
    } else {
      this.dialogRef.close();
    }
  }
}
